import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Zap,
  ShieldCheck,
  Headphones,
  TrendingUp,
  MessageCircle,
  ArrowUpRight,
  Sparkles,
} from "lucide-react";
import { translations, WHATSAPP_LINK, WEBSITE_LINK, getInitialLang, saveLang } from "../i18n";
import { SiteHeader } from "../components/SiteHeader";
import { LeadForm } from "../components/LeadForm";
import { SpinWheel } from "../components/SpinWheel";
import { CountdownTimer } from "../components/CountdownTimer";
import { WinnerToasts } from "../components/WinnerToasts";
import { ReturningVisitorPopup } from "../components/ReturningVisitorPopup";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/02f4d7be-4fab-4756-8c13-c9a159ebb41e/images/eeb56132a371fec6a9ac63e380767faf60dbd5c8f0b16d264865b7d0ac3c3b73.jpeg";
const ACTION_IMG =
  "https://images.unsplash.com/photo-1593341646782-e0b495cff86d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const BRAND_1 =
  "https://customer-assets-lqy194kg.emergentagent.net/job_02f4d7be-4fab-4756-8c13-c9a159ebb41e/artifacts/hju5mj2o_ChatGPT%20Image%20Aug%2027%2C%202026%2C%2002_51_52%20PM.png";
const BRAND_2 =
  "https://customer-assets-lqy194kg.emergentagent.net/job_02f4d7be-4fab-4756-8c13-c9a159ebb41e/artifacts/8ziwwoqm_ChatGPT%20Image%20Aug%2024%2C%202026%2C%2001_02_12%20PM.png";

const ease = [0.22, 1, 0.36, 1];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.8, ease, delay }}
    className={className}
  >
    {children}
  </motion.div>
);

const MaskLine = ({ children, delay = 0 }) => (
  <span className="mask-reveal">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, ease, delay }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Landing() {
  const [lang, setLangState] = useState(getInitialLang);
  const setLang = (l) => {
    saveLang(l);
    setLangState(l);
  };
  const [spinOpen, setSpinOpen] = useState(false);
  const t = translations[lang];
  const heroRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const heroTextY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  // Spin wheel: exit-intent (mouse leaves top of viewport) + timed fallback.
  useEffect(() => {
    if (sessionStorage.getItem("cric1x_spin_seen")) return;
    const trigger = () => {
      if (sessionStorage.getItem("cric1x_spin_seen")) return;
      sessionStorage.setItem("cric1x_spin_seen", "1");
      setSpinOpen(true);
    };
    const onMouseOut = (e) => {
      if (!e.relatedTarget && e.clientY <= 0) trigger();
    };
    const timer = setTimeout(trigger, 20000);
    document.addEventListener("mouseout", onMouseOut);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mouseout", onMouseOut);
    };
  }, []);

  const why = [
    { icon: Zap, t: t.why1_t, d: t.why1_d },
    { icon: ShieldCheck, t: t.why2_t, d: t.why2_d },
    { icon: Headphones, t: t.why3_t, d: t.why3_d },
    { icon: TrendingUp, t: t.why4_t, d: t.why4_d },
  ];

  return (
    <div className="relative min-h-screen bg-obsidian font-body text-white">
      <div className="noise-overlay" />

      {/* NAV */}
      <SiteHeader lang={lang} setLang={setLang} />

      {/* HERO */}
      <section id="top" ref={heroRef} className="relative h-[100svh] overflow-hidden">
        <motion.div style={{ y: heroY, scale: heroScale }} className="absolute inset-0">
          <img src={HERO_IMG} alt="Cricket stadium" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/70 to-obsidian/40" />
          <div className="absolute inset-0 bg-gradient-to-r from-obsidian/90 via-transparent to-transparent" />
        </motion.div>

        <motion.div
          style={{ y: heroTextY }}
          className="relative z-10 mx-auto flex h-full max-w-7xl flex-col justify-end px-5 pb-20"
        >
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.8 }}
            className="mb-5 inline-flex w-max items-center gap-2 border border-gold bg-gold/15 px-4 py-2.5 backdrop-blur-sm gold-glow"
            data-testid="hero-offer-badge"
          >
            <Sparkles size={15} className="text-gold" />
            <span className="font-display text-sm font-bold uppercase tracking-wide text-gold">{t.hero_badge}</span>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 1 }}
            className="mb-5 font-mono text-[12px] uppercase tracking-[0.35em] text-gold"
          >
            {t.hero_kicker}
          </motion.p>
          <h1 className="font-display text-6xl font-bold uppercase leading-[0.88] tracking-tighter sm:text-7xl lg:text-8xl">
            <MaskLine delay={0.3}>{t.hero_line1}</MaskLine>
            <MaskLine delay={0.42}>
              <span className="gold-text">{t.hero_line2}</span>
            </MaskLine>
            <MaskLine delay={0.54}>{t.hero_line3}</MaskLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8 }}
            className="mt-6 max-w-xl text-base text-white/70 sm:text-lg"
          >
            {t.hero_sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.8 }}
            className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap"
          >
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center gap-2 bg-gold px-8 py-4 font-display text-sm font-bold uppercase tracking-wide text-black transition-transform hover:scale-[0.97] gold-glow"
              data-testid="hero-join-cta"
            >
              {t.cta_join} <ArrowUpRight size={18} />
            </a>
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noreferrer"
              className="group flex items-center justify-center gap-2 bg-wa px-8 py-4 font-display text-sm font-semibold uppercase tracking-wide text-white transition-transform hover:scale-[0.97]"
              data-testid="hero-whatsapp-cta"
            >
              <MessageCircle size={18} /> {t.cta_whatsapp}
            </a>
            <button
              onClick={() => setSpinOpen(true)}
              className="flex items-center justify-center gap-2 border border-gold/60 bg-gold/5 px-8 py-4 font-display text-sm font-semibold uppercase tracking-wide text-gold transition-colors hover:bg-gold/15"
              data-testid="hero-spin-cta"
            >
              <Sparkles size={18} /> {t.cta_claim}
            </button>
            <a
              href={WEBSITE_LINK}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center gap-2 border border-white/25 px-8 py-4 font-display text-sm font-semibold uppercase tracking-wide text-white transition-colors hover:border-gold hover:text-gold"
              data-testid="hero-website-cta"
            >
              {t.cta_website} <ArrowUpRight size={18} />
            </a>
          </motion.div>
        </motion.div>
      </section>

      {/* MARQUEE */}
      <div className="overflow-hidden border-y border-white/10 bg-black py-5">
        <div className="flex w-max animate-marquee whitespace-nowrap">
          {[...Array(2)].map((_, k) => (
            <span key={k} className="flex items-center">
              {[...Array(6)].map((_, i) => (
                <span
                  key={i}
                  className="mx-6 font-display text-4xl font-semibold uppercase tracking-tight text-stroke sm:text-6xl"
                >
                  {t.marquee}
                </span>
              ))}
            </span>
          ))}
        </div>
      </div>

      {/* OFFERS BENTO */}
      <section id="offers" className="mx-auto max-w-7xl px-5 py-24 lg:py-32">
        <Reveal>
          <p className="font-mono text-[12px] uppercase tracking-[0.35em] text-gold">
            {t.offers_kicker}
          </p>
          <h2 className="mt-3 max-w-3xl font-display text-5xl font-bold uppercase leading-none tracking-tighter sm:text-6xl">
            {t.offers_title}
          </h2>
        </Reveal>

        <div className="mt-14 grid grid-cols-12 gap-5">
          <Reveal className="col-span-12 md:col-span-8">
            <div className="group relative h-full overflow-hidden border-2 border-gold bg-surface p-8 gold-glow sm:p-10" data-testid="offer-10-card">
              <div className="absolute right-0 top-0 z-10 bg-gold px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-black">
                {t.offer1_badge}
              </div>
              <img
                src={ACTION_IMG}
                alt="Cricket action"
                className="absolute right-0 top-0 h-full w-1/2 object-cover opacity-25 transition-opacity duration-500 group-hover:opacity-40"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-surface via-surface/80 to-transparent" />
              <div className="relative">
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">
                  {t.offer1_tag}
                </p>
                <p className="mt-6 font-display text-8xl font-bold leading-none gold-text sm:text-9xl">
                  {t.offer1_value}
                </p>
                <p className="mt-6 max-w-md text-white/70">{t.offer1_desc}</p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="col-span-12 md:col-span-4">
            <div className="flex h-full flex-col justify-between border border-white/10 bg-surface p-8">
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/50">
                {t.offer2_tag}
              </p>
              <div>
                <p className="font-display text-7xl font-bold leading-none gold-text">
                  {t.offer2_value}
                </p>
                <p className="mt-4 text-sm text-white/70">{t.offer2_desc}</p>
                <div className="mt-6 border-t border-white/10 pt-5">
                  <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
                    {t.offer_ends}
                  </p>
                  <CountdownTimer />
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15} className="col-span-12 md:col-span-4">
            <div className="flex h-full flex-col justify-between border border-white/10 bg-surface p-8">
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/50">
                {t.offer3_tag}
              </p>
              <div>
                <p className="font-display text-7xl font-bold leading-none gold-text">
                  {t.offer3_value}
                </p>
                <p className="mt-4 text-sm text-white/70">{t.offer3_desc}</p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.2} className="col-span-12 md:col-span-8">
            <button
              onClick={() => setSpinOpen(true)}
              className="flex h-full w-full items-center justify-between border border-gold/40 bg-gold/5 p-8 text-left transition-colors hover:bg-gold/10"
              data-testid="offers-spin-cta"
            >
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">
                  Bonus Wheel
                </p>
                <p className="mt-3 font-display text-3xl font-bold uppercase leading-none sm:text-4xl">
                  {t.cta_claim}
                </p>
              </div>
              <Sparkles className="text-gold gold-glow" size={44} />
            </button>
          </Reveal>
        </div>
      </section>

      {/* SHOWCASE */}
      <section className="border-y border-white/10 bg-black py-24">
        <div className="mx-auto max-w-7xl px-5">
          <Reveal className="grid gap-5 lg:grid-cols-2">
            <div className="overflow-hidden border border-white/10">
              <img src={BRAND_2} alt="Cric1x brand" className="h-full w-full object-cover" />
            </div>
            <div className="overflow-hidden border border-white/10">
              <img src={BRAND_1} alt="Cric1x live cricket" className="h-full w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY + FORM */}
      <section id="register" className="mx-auto max-w-7xl px-5 py-24 lg:py-32">
        <div className="grid gap-16 lg:grid-cols-2">
          <div id="why">
            <Reveal>
              <p className="font-mono text-[12px] uppercase tracking-[0.35em] text-gold">
                {t.nav_why}
              </p>
              <h2 className="mt-3 font-display text-5xl font-bold uppercase leading-none tracking-tighter sm:text-6xl">
                {t.why_title}
              </h2>
            </Reveal>
            <div className="mt-12 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2">
              {why.map((w, i) => (
                <Reveal key={i} delay={i * 0.08}>
                  <div className="h-full bg-surface p-7">
                    <w.icon className="text-gold" size={28} />
                    <p className="mt-5 font-display text-xl font-semibold uppercase">{w.t}</p>
                    <p className="mt-2 text-sm text-white/60">{w.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal delay={0.1}>
            <div className="border border-gold/20 bg-surface p-8 sm:p-10">
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">
                {t.form_kicker}
              </p>
              <h3 className="mt-3 font-display text-4xl font-bold uppercase leading-none gold-text sm:text-5xl">
                {t.form_title}
              </h3>
              <p className="mt-3 text-sm text-white/60">{t.form_sub}</p>
              <div className="mt-9">
                <LeadForm t={t} />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TRUST + PAYMENTS */}
      <section className="border-t border-white/10 bg-black py-16">
        <div className="mx-auto max-w-7xl px-5">
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
            {[
              { icon: ShieldCheck, label: t.trust_ssl },
              { icon: ShieldCheck, label: t.trust_secure },
              { icon: Headphones, label: t.trust_support },
            ].map((b, i) => (
              <div key={i} className="flex items-center gap-2 text-white/60">
                <b.icon size={18} className="text-gold" />
                <span className="font-mono text-[12px] uppercase tracking-widest">{b.label}</span>
              </div>
            ))}
          </div>
          <p className="mt-10 text-center font-mono text-[12px] uppercase tracking-[0.3em] text-white/40">
            {t.pay_title}
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            {["UPI", "Paytm", "PhonePe", "GPay", "Net Banking", "Bank Transfer"].map((p) => (
              <span
                key={p}
                className="border border-white/10 bg-surface px-5 py-3 font-display text-sm font-medium uppercase tracking-wide text-white/70"
              >
                {p}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="mx-auto max-w-7xl px-5 py-16">
        <div className="flex flex-col items-start justify-between gap-8 border-b border-white/10 pb-12 md:flex-row md:items-end">
          <div>
            <p className="font-display text-4xl font-bold tracking-tight">
              CRIC<span className="gold-text">1X</span>
            </p>
            <p className="mt-2 font-mono text-[12px] uppercase tracking-[0.3em] text-white/40">
              Play · Predict · Win
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center gap-2 bg-wa px-6 py-3 font-display text-sm font-semibold uppercase tracking-wide text-white transition-transform hover:scale-[0.97]"
              data-testid="footer-whatsapp-cta"
            >
              <MessageCircle size={16} /> {t.cta_whatsapp}
            </a>
            <a
              href={WEBSITE_LINK}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center gap-2 border border-white/25 px-6 py-3 font-display text-sm font-semibold uppercase tracking-wide text-white transition-colors hover:border-gold hover:text-gold"
              data-testid="footer-website-cta"
            >
              {t.cta_website} <ArrowUpRight size={16} />
            </a>
          </div>
        </div>
        <p className="mt-8 max-w-2xl text-xs leading-relaxed text-white/40">{t.footer_disclaimer}</p>
        <p className="mt-4 font-mono text-[11px] uppercase tracking-widest text-white/30">
          © {new Date().getFullYear()} Cric1x. {t.footer_rights}
        </p>
      </footer>

      {/* Sticky WhatsApp */}
      <a
        href={WHATSAPP_LINK}
        target="_blank"
        rel="noreferrer"
        className="fixed bottom-5 right-5 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-wa gold-glow transition-transform hover:scale-110"
        data-testid="sticky-whatsapp"
        aria-label="WhatsApp"
      >
        <MessageCircle className="text-white" size={26} />
      </a>

      <SpinWheel open={spinOpen} onClose={() => setSpinOpen(false)} t={t} />
      <WinnerToasts lang={lang} />
      <ReturningVisitorPopup lang={lang} />
    </div>
  );
}
